// assets/page-size-select.js
// <page-size-select> — the "Show" control: how many products a shopper sees per
// display page.
//
// It owns almost nothing. Choosing a size is handed straight to
// <collection-pager>, which re-divides the products already on the page; this
// element only keeps what the shopper sees in the dropdown honest.
//
// That job exists because the server cannot render the right selection. Liquid
// cannot read query-string parameters (docs/adr/0002), so every server render —
// including the ones the filter form triggers, which replace this markup
// wholesale — comes back showing the merchant's default. So the value is
// re-synced whenever the element is (re)connected, whenever history moves, and
// whenever the grid is re-divided.
//
// What it syncs to is the pager, not the URL. The pager weighs the URL, the
// shopper's remembered preference and the merchant's setting, and a remembered
// preference applies with the URL left clean — so reading the URL here would show
// the default while the grid showed 40. Asking the one element that already
// decided keeps the two from disagreeing.
//
// The select carries `name="per_page"` and sits inside the filter form on
// purpose: that is what carries the chosen size through a filter or sort change.
// facets.js recognises it and skips its usual re-render, since a size change
// needs no server round trip at all.
class PageSizeSelect extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.sync = this.sync.bind(this);
  }

  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;
    this.sync();
    this.select.addEventListener('change', this.onChange);
    // Follow the size whenever it could have moved: history navigation, and any
    // re-division of the grid — including ones this control did not start.
    window.addEventListener('popstate', this.sync);
    document.addEventListener('collection-pager:render', this.sync);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.sync);
    document.removeEventListener('collection-pager:render', this.sync);
  }

  get value() {
    return this.select ? this.select.value : null;
  }

  // The size actually in force. A render event states it outright; otherwise the
  // pager is asked. The URL is the last resort rather than the first, for a grid
  // whose pager has not registered yet — it can only ever be missing the
  // remembered preference, never wrong about an explicit one.
  sync(event) {
    if (!this.select) return;
    const announced = event && event.detail ? event.detail.pageSize : null;
    const pager = window.CollectionPager && window.CollectionPager.current();
    this.displaySize(
      announced || (pager && pager.pageSize) || new URLSearchParams(window.location.search).get(this.select.name)
    );
  }

  // A size nobody can pick is ignored rather than added to the dropdown.
  displaySize(size) {
    if (!size || !this.select) return;
    const value = String(size);
    const offered = Array.from(this.select.options).some((option) => option.value === value);
    if (offered) this.select.value = value;
  }

  onChange() {
    const pager = window.CollectionPager && window.CollectionPager.current();
    if (!pager) return;
    // Handed across as the shopper picked it. "all" is a size, not a number, and
    // coercing it would make it NaN; the pager validates the token either way.
    pager.setPageSize(this.select.value);
    PageSizeSelect.syncAll(this.select.value, this);
  }

  // Desktop and mobile drawer each hold a copy; a change in one shows in both.
  static syncAll(value, origin) {
    document.querySelectorAll('page-size-select').forEach((element) => {
      if (element === origin || typeof element.displaySize !== 'function') return;
      element.displaySize(value);
    });
  }
}

customElements.define('page-size-select', PageSizeSelect);
